import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { PlatformRouteView } from "@tdelivr/api-client/generated/experience/api-gateway.js";

import { GATEWAY_ROUTES_SCOPE } from "../navigation.ts";
import {
  PUBLISH_REFUSAL,
  routeRow,
  statesPresent,
  stateOf,
  tableBoundNote,
} from "../gateway-routes.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `gateway` in `16 WP15 Platform Machinery.dc.html`: the route table, across every tenant.
 *
 * ```
 * gateway:['Gateway routes','API-01','Publishing a route changes authorisation for the whole
 *   platform. Every version needs a requester and a different approver.',
 *   [['Routes','142','done','published'],['Awaiting approval','2','human','needs approver'],
 *    ['Rate-limited now','3','caution','bursting'],['Disabled','1','closed','by approval']],
 *   ['Route','Environment','Scopes'],
 *   [['/v1/ride-offers','production','ride-offer:read + workload','done','published v7'], …],
 *   'lock','Uses the approval pattern from deliverable 11: requestedBy and approvedBy must differ.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                    | source                                            |
 * | ---------------------------------------- | ------------------------------------------------- |
 * | the rows                                 | `EXP-001 GET /v1/platform/gateway/routes` (ADR 0051) |
 * | `Route`                                  | `routes.pathPrefix`                                |
 * | `Environment`                            | `environment`, which is the gateway's. Q-191       |
 * | `Scopes`                                 | `routes.requiredScopes`                            |
 * | `published v7`                           | `routes.version`                                   |
 * | `+ workload` in the `Scopes` cell        | **nothing** — a workload route is refused at load. Q-188 |
 * | `v12 awaiting approval`, `rate limited`, `by approval` | **nothing** — only loaded routes are answered. Q-188, Q-191 |
 * | the requester and the approver           | **nothing anywhere on this platform**. Q-188       |
 * | the four KPI tiles                       | **nothing** — R-04, Q-178; three are Q-188's       |
 * | `New gateway route version`              | **nothing** — API-01's write is `/internal/v1`     |
 *
 * **A platform-scoped read was not enough to finish this page.** It made the three columns drawable
 * — they were already published, and the one thing wrong was that the listing answered one tenant's
 * rows — and it did not and could not give the page its requester and approver, which is the
 * sentence its own purpose line is made of. API-01 publishes both under `/internal/v1`, which the
 * gateway does not expose, and **a column nothing a browser can call maintains is not added**:
 * there is no fourth column here, and the absence is recorded in Q-188 rather than drawn as a row
 * of dashes under a heading that promises an approval.
 *
 * `../gateway-routes.ts` is where each of those absences is argued, and where the one reachable badge
 * is defended rather than apologised for.
 *
 * **The source chip reads `EXP-001 · API-01` where the design's reads `API-01`**, and that is the
 * one place the words on this page are not the design's. The chip is where a reader learns which
 * team owns what they are looking at, and the rows on this board are EXP-001's: API-01 publishes the
 * policy and the gateway answers which of it loaded, which ADR 0029 made the arrangement. Naming
 * only API-01 would send a reader asking about a row to the service that does not hold it.
 * `navigation.ts` lists the same two owners, in the same order, and this is recorded as **Q-188**
 * with what was done meanwhile rather than left as a silent difference.
 */

const gateway = services.experience__api_gateway;

export interface GatewayRoutesProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Machinery › Gateway routes`, which is the design's own trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Gateway routes";
/** See the header: the design's chip reads `API-01` and the rows are EXP-001's. Q-188. */
const SOURCE = "EXP-001 · API-01";
const DESCRIPTION =
  "Publishing a route changes authorisation for the whole platform. Every version needs a requester and a different approver.";
const COLUMNS = ["Route", "Environment", "Scopes"] as const;
const NOTE =
  "Uses the approval pattern from deliverable 11: requestedBy and approvedBy must differ.";
/** The design's own `noteK` for this page is `lock`, which its `NK` map draws in the accent tint. */
const NOTE_KIND = "lock" as const;
/** The design's `FORMS.gateway` primary button, word for word. */
const PRIMARY_LABEL = "New route version";

export function GatewayRoutes({ transport, session, crumbs }: GatewayRoutesProps): ReactNode {
  const [routes, setRoutes] = useState<readonly PlatformRouteView[] | undefined>(undefined);
  const [environment, setEnvironment] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRoutes(undefined);
    setFailure(undefined);

    /*
     * Asked for before the call, not discovered from the refusal. ADR 0049 §3, and the notification
     * bell's three-console 403 is what happens without it.
     */
    if (!session.scopes.has(GATEWAY_ROUTES_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [GATEWAY_ROUTES_SCOPE] });
      return;
    }

    try {
      const client = gateway.createApiGatewayClient(transport);
      /*
       * `getPlatformGatewayRouteTable`, not `getGatewayRouteTable`.
       *
       * The second is "filtered to the tenant on the caller's verified token", which on this page
       * would be one tenant's routes under a heading about authorisation for the whole platform.
       * ADR 0051 published them side by side and left the first untouched.
       *
       * **No pagination, and that is the operation rather than a shortcut.** A gateway's loaded
       * table is held in memory and answered whole; it has no cursor because there is no page
       * boundary to cross. So the board is the table, and `tableBoundNote` says what the table is
       * rather than how much of it arrived.
       */
      const table = await client.getPlatformGatewayRouteTable();
      setRoutes(table.routes);
      setEnvironment(table.environment);
    } catch (error) {
      setFailure(stateForError(error, [GATEWAY_ROUTES_SCOPE]));
    }
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows =
    routes === undefined || environment === undefined
      ? undefined
      : routes
          .filter((route) => state === "" || stateOf(route).label === state)
          .map((route) => routeRow(route, environment));

  const bound = tableBoundNote(routes, environment);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      /*
       * The empty state used to read "A route appears here once API-01 publishes and approves
       * one and the gateway accepts it", and that sentence sent an operator to do the one thing
       * that does not work. Four route versions were published and approved on 26 October and
       * this board stayed empty, because **nothing carries a published version into the table
       * the gateway serves**: EXP-001 builds its table from a file,
       * `platform/deploy/gateway-route-table.json`, generated empty on purpose
       * — a route in it is an approved route-policy version, which is API-01's to publish and
       * the generator's to invent nowhere.
       *
       * So the sentence says what is actually missing, which is the join and not the publishing.
       * Q-269 carries the decision about which of the two owns a route.
       */
      purpose="This gateway has loaded no route for any tenant, and publishing one will not change that on its own: it serves a generated route table, and nothing yet carries API-01’s published route versions into it. Q-269."
      primary={{ label: PRIMARY_LABEL, refusal: PUBLISH_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (routes === undefined ? [] : statesPresent(routes)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "lock", text: NOTE }}
      {...(bound === undefined ? {} : { footnote: bound })}
      exportName="gateway-routes"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
